import { useId, useRef } from 'react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { useToast } from '../../../components/ui/ToastProvider'
import type { BomShareResult } from '../types'

export interface BomShareModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly result: BomShareResult | null
}

function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'This link doesn’t expire.'
  const date = new Date(expiresAt)
  if (Number.isNaN(date.getTime())) return 'This link doesn’t expire.'
  return `Link expires ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.`
}

/**
 * UID-296 — the "Share" header action's result: `POST /boms/{bomId}/share`
 * returns the raw share token EXACTLY ONCE (only its hash is persisted
 * server-side, per `shareBom`'s doc comment in `useBomDetail.ts`), so this
 * modal is the one place the user can see/copy it. Modeled on the
 * "readonly input + Copy button" pattern the RFQ vertical's `ShareHandoffModal`
 * established, rather than inventing a second shape for the same idea. That
 * component was retired by UID-438 (ADR-0143 D1) — its replacement
 * (`RfqShareQuoteModal`) copies a link from a button with no visible input, so
 * THIS modal is now the only surviving instance of the pattern. Kept as-is: a
 * one-time-visible token is precisely the case that needs the token on screen,
 * not hidden behind a copy button.
 */
export function BomShareModal({ open, onClose, result }: BomShareModalProps) {
  const { showToast } = useToast()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const fieldId = useId()

  async function handleCopy() {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.token)
      showToast('Share link copied to clipboard.', 'success')
    } catch {
      showToast('Couldn’t copy automatically — select the code and copy it manually.', 'error')
      inputRef.current?.select()
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="BOM share link created">
      {result && (
        <div className="bom-share-modal-body">
          <p role="alert">
            Copy this now — for security, it won’t be shown again once you close this dialog.
          </p>
          <div className="bom-share-modal-row">
            <label htmlFor={fieldId}>Share link</label>
            <input
              id={fieldId}
              ref={inputRef}
              readOnly
              className="bom-share-modal-input"
              value={result.token}
            />
            <Button variant="outline" size="sm" onClick={handleCopy}>
              Copy
            </Button>
          </div>
          <p className="caption-text">{formatExpiry(result.expiresAt)}</p>
        </div>
      )}
    </Modal>
  )
}
